// 試用版申込フォーム全体の入力内容・状態管理・API通信を担う Pinia ストア

// src/store/useExperienceFormStore.ts
import { defineStore } from "pinia";
import axios from "axios";
import { getApiEndpointWithSubdomainReplace } from "../config/apiConfig.ts";

// --- フォーム型定義 -------------------------------------------------
interface ManagerInfo {
  company: string | null;
  zip: string | null;
  prefecture: string | null;
  city: string | null;
  address1: string | null;
  building: string | null;
  department: string | null;
  name: string | null;
  furigana: string | null;
  email: string | null;
  tel: string | null;
  mailDelivery: string | null;
  subName: string | null;
  subEmail: string | null;
}

interface AgencyInfo {
  company: string | null;
  zip: string | null;
  prefecture: string | null;
  city: string | null;
  address1: string | null;
  building: string | null;
  department: string | null;
  name: string | null;
  furigana: string | null;
  email: string | null;
  tel: string | null;
  mailDelivery: string | null;
  subName: string | null;
  subEmail: string | null;
}

export type DeliveryType = "customer" | "manager" | "agency" | "other";

interface DeliveryInfoForm {
  types: DeliveryType[]; // アカウント情報の送付先（複数選択可）
  name: string | null;
  email: string | null;
}

interface OptionsForm {
  windows: boolean;
  idManager: boolean;
  fingerprint: boolean;
  iccard: boolean;
}

interface RentalForm {
  quantity: number;
  receiver: "customer" | "manager" | "agency";
}

interface CustomerIdCheckResponse {
  resultCode: number;
  message: string;
}

interface ExperienceSubmitPayload {
  command: "experienceAdd";
  customerId: string;
  optionSettings: string;
  clientInfo: {
    companyName: string | null;
    location: string;
    deptTitle: string | null;
    userName: string | null;
    userName2: string | null;
    email: string | null;
    phone: string | null;
    optInMailing: boolean;
    postCode: string | null;
    buildingName: string | null;
  };
  adminInfo: {
    companyName: string | null;
    location: string;
    deptTitle: string | null;
    userName: string | null;
    userName2: string | null;
    email: string | null;
    phone: string | null;
    optInMailing: boolean;
    operatorName: string | null;
    operatorEmail: string | null;
  } | null;
  // 運用者等（operator）は管理者情報のみ。代理店には入力欄が無く、サーバー側にも項目が無い
  partnerInfo: {
    companyName: string | null;
    location: string;
    deptTitle: string | null;
    userName: string | null;
    userName2: string | null;
    email: string | null;
    phone: string | null;
    optInMailing: boolean;
  } | null;
  deliveryInfo: {
    destination: DeliveryType[];
    userName: string | null;
    email: string | null;
  };
  rental?: {
    quantity: number;
    receiver: string;
  } | null;
  endDate: string;
}

export interface ExperienceFormAll {
  // STEP1
  company: string | null;
  zip: string | null;
  prefecture: string | null;
  city: string | null;
  address1: string | null;
  building: string | null;
  department: string | null;
  name: string | null;
  furigana: string | null;
  email: string | null;
  tel: string | null;
  mailDelivery: string | null;
  manager: ManagerInfo;
  agency: AgencyInfo;
  delivery: DeliveryInfoForm;

  managerEnabled: boolean;
  agencyEnabled: boolean;

  // STEP2
  customerId: string;
  options: OptionsForm;
  rental: RentalForm;
  agreeTerms: boolean;
  agreePrivacy: boolean;
}

export interface ExperienceFormResponse {
  resultCode: number;
  message: string;
  password?: string;
  sysAdminPassword?: string;
}

export interface ExperienceSubmitApiResponse {
  resultCode: number;
  message: string;
  password?: string;
  sysAdminPassword?: string;
}

// --- LocalStorage キー -------------------------------------------------
const STORAGE_KEY = "experienceFormData";

// --- LocalStorage ヘルパー -------------------------------------------------
export function saveToLocalStorage(form: ExperienceFormAll) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
  } catch (e) {
    console.error("フォームデータの保存に失敗しました:", e);
  }
}

// 送付先が単一選択だった頃に保存されたデータを、複数選択の形式に変換する。
// delivery 自体が欠けているデータも初期値で補い、画面が落ちないようにする
function migrateDeliveryTypes(form: any): any {
  if (!form || typeof form !== "object") {
    return form;
  }

  const delivery = form.delivery;
  if (delivery && Array.isArray(delivery.types)) {
    return form;
  }

  const legacyType =
    typeof delivery?.type === "string" && delivery.type
      ? delivery.type
      : "customer";

  form.delivery = {
    types: [legacyType],
    name: delivery?.name ?? null,
    email: delivery?.email ?? null,
  };
  return form;
}

function loadFromLocalStorage(): ExperienceFormAll | null {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return migrateDeliveryTypes(JSON.parse(data));
    }
  } catch (e) {
    console.error("フォームデータの読み込みに失敗しました:", e);
  }
  return null;
}

function clearLocalStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error("フォームデータのクリアに失敗しました:", e);
  }
}

// --- 初期状態（デフォルト値） -------------------------------------------------
function getInitialFormState(): ExperienceFormAll {
  return {
    company: null,
    zip: null,
    prefecture: null,
    city: null,
    address1: null,
    building: null,
    department: null,
    name: null,
    furigana: null,
    email: null,
    tel: null,
    mailDelivery: null,
    managerEnabled: false,
    agencyEnabled: false,
    manager: {
      company: null,
      zip: null,
      prefecture: null,
      city: null,
      address1: null,
      building: null,
      department: null,
      name: null,
      furigana: null,
      email: null,
      tel: null,
      mailDelivery: null,
      subName: null,
      subEmail: null,
    },
    agency: {
      company: null,
      zip: null,
      prefecture: null,
      city: null,
      address1: null,
      building: null,
      department: null,
      name: null,
      furigana: null,
      email: null,
      tel: null,
      mailDelivery: null,
      subName: null,
      subEmail: null,
    },
    delivery: { types: ["customer"], name: null, email: null },
    customerId: "",
    options: {
      windows: false,
      idManager: false,
      fingerprint: false,
      iccard: false,
    },
    rental: { quantity: 1, receiver: "customer" },
    agreeTerms: false,
    agreePrivacy: false,
  };
}

// --- ストア本体 -------------------------------------------------
export const useExperienceFormStore = defineStore("experienceForm", {
  state: (): {
    form: ExperienceFormAll;
    loading: boolean;
    error: string;
    lastResponse: ExperienceFormResponse | null;
    isSubmitted: boolean;
  } => {
    // LocalStorageから復元を試みる
    const saved = loadFromLocalStorage();
    return {
      form: saved || getInitialFormState(),
      loading: false,
      error: "",
      lastResponse: null,
      isSubmitted: false,
    };
  },

  actions: {
    // STEP1 から保存
    setStep1(data: any) {
      this.form.company = data.company;
      this.form.zip = data.zip;
      this.form.prefecture = data.prefecture;
      this.form.city = data.city;
      this.form.address1 = data.address1;
      this.form.building = data.building;
      this.form.department = data.department;
      this.form.name = data.name;
      this.form.furigana = data.furigana;
      this.form.email = data.email;
      this.form.tel = data.tel;
      this.form.mailDelivery = data.mailDelivery;
      this.form.managerEnabled = !!data.managerEnabled;
      this.form.agencyEnabled = !!data.agencyEnabled;

      if (data.manager) {
        this.form.manager = { ...this.form.manager, ...data.manager };
      }
      if (data.agency) {
        this.form.agency = { ...this.form.agency, ...data.agency };
      }
      if (data.delivery) {
        this.form.delivery = { ...this.form.delivery, ...data.delivery };
      }
    },

    // STEP2 から保存
    setStep2(data: any) {
      this.form.customerId = data.customerId;
      if (data.options) {
        this.form.options = { ...this.form.options, ...data.options };
      }
      if (data.rental) {
        this.form.rental = { ...this.form.rental, ...data.rental };
      }
      this.form.agreeTerms = data.agreeTerms;
      this.form.agreePrivacy = data.agreePrivacy;
    },

    // payload生成
    buildSubmitPayload(): ExperienceSubmitPayload {
      const f = this.form;

      // 住所を結合するヘルパー関数（null/undefinedを空文字として扱う）
      const buildLocation = (
        prefecture: string | null,
        city: string | null,
        address1: string | null,
        building: string | null,
      ): string => {
        const parts = [
          prefecture || "",
          city || "",
          address1 || "",
          building || "",
        ].filter((part) => part !== "");
        return parts.join("") || "";
      };

      const userInfo = {
        companyName: f.company,
        location: buildLocation(f.prefecture, f.city, f.address1, f.building),
        deptTitle: f.department,
        userName: f.name,
        userName2: f.furigana,
        email: f.email,
        phone: f.tel,
        optInMailing: f.mailDelivery === "希望",
        postCode: f.zip,
        buildingName: f.building,
      };

      const hasManager =
        f.manager && Object.values(f.manager).some((v) => v && v !== "");
      const adminInfo = hasManager
        ? {
            companyName: f.manager.company,
            location: buildLocation(
              f.manager.prefecture,
              f.manager.city,
              f.manager.address1,
              f.manager.building,
            ),
            deptTitle: f.manager.department,
            userName: f.manager.name,
            userName2: f.manager.furigana,
            email: f.manager.email,
            phone: f.manager.tel,
            optInMailing: f.manager.mailDelivery === "希望",
            operatorName: f.manager.subName,
            operatorEmail: f.manager.subEmail,
          }
        : null;

      const hasAgency =
        f.agency && Object.values(f.agency).some((v) => v && v !== "");
      const partnerInfo = hasAgency
        ? {
            companyName: f.agency.company,
            location: buildLocation(
              f.agency.prefecture,
              f.agency.city,
              f.agency.address1,
              f.agency.building,
            ),
            deptTitle: f.agency.department,
            userName: f.agency.name,
            userName2: f.agency.furigana,
            email: f.agency.email,
            phone: f.agency.tel,
            optInMailing: f.agency.mailDelivery === "希望",
          }
        : null;

      // 氏名・メールアドレスは「その他」を選んだときだけ入力される
      const isOtherDelivery = f.delivery.types.includes("other");
      const deliveryInfo = {
        destination: f.delivery.types,
        userName: isOtherDelivery ? f.delivery.name : "",
        email: isOtherDelivery ? f.delivery.email : "",
      };

      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3);
      const endDateStr = endDate.toISOString().split("T")[0]!;

      // 指紋認証オプションが選択されている場合のみrentalを含める
      const rental = f.options.fingerprint
        ? {
            quantity: f.rental.quantity,
            receiver: f.rental.receiver,
          }
        : null;

      return {
        command: "experienceAdd",
        customerId: f.customerId,
        optionSettings: JSON.stringify(f.options),
        clientInfo: userInfo,
        adminInfo,
        partnerInfo,
        deliveryInfo,
        rental,
        endDate: endDateStr,
      };
    },

    // CustomerId 重複チェック
    async checkCustomerId(customerId: string): Promise<boolean> {
      if (!customerId) return false;

      try {
        this.loading = true;
        this.error = "";

        const apiUrl = getApiEndpointWithSubdomainReplace();

        const res = await axios.post<CustomerIdCheckResponse>(
          apiUrl,
          {
            command: "check",
            customerId,
          },
          {
            headers: { "Content-Type": "application/json" },
          },
        );

        // resultCode の扱いは API 仕様に合わせて調整
        if (res.data.resultCode !== 0) {
          this.error = res.data.message || "このCustomerIdは使用されています";
          return false;
        }

        return true;
      } catch (e: any) {
        this.error = "CustomerId の確認に失敗しました";
        return false;
      } finally {
        this.loading = false;
      }
    },

    // 郵便番号から住所を取得（zipcloud）
    async searchAddress(
      target: "customer" | "manager" | "agency",
    ): Promise<boolean> {
      const form =
        target === "customer"
          ? this.form
          : target === "manager"
            ? this.form.manager
            : this.form.agency;

      if (!form.zip) {
        this.error = "郵便番号を入力してください";
        return false;
      }

      // ハイフン除去
      const zip = form.zip.replace("-", "");

      try {
        this.loading = true;
        this.error = "";

        const res = await axios.get(
          "https://zipcloud.ibsnet.co.jp/api/search",
          {
            params: { zipcode: zip },
          },
        );

        const result = res.data.results?.[0];
        if (!result) {
          this.error = "該当する住所が見つかりませんでした";
          return false;
        }

        // 住所反映
        form.prefecture = result.address1; // 都道府県
        form.city = result.address2; // 市区町村
        form.address1 = result.address3; // 町名

        return true;
      } catch (e) {
        this.error = "住所の取得に失敗しました";
        return false;
      } finally {
        this.loading = false;
      }
    },

    // 送信
    async submit(): Promise<ExperienceSubmitApiResponse | null> {
      this.loading = true;
      this.error = "";
      this.lastResponse = null;

      try {
        const body = this.buildSubmitPayload();

        const apiUrl = getApiEndpointWithSubdomainReplace();

        const res = await axios.post<ExperienceSubmitApiResponse>(
          apiUrl,
          body,
          {
            headers: { "Content-Type": "application/json" },
          },
        );

        this.lastResponse = res.data as any;
        this.isSubmitted = true; // 送信完了フラグを立てる
        return res.data;
      } catch (e: any) {
        this.error = e?.message ?? "送信に失敗しました。";
        return null;
      } finally {
        this.loading = false;
      }
    },

    // フォームリセット
    resetForm() {
      this.form = getInitialFormState();
      this.lastResponse = null;
      this.error = "";
      this.isSubmitted = false;

      // LocalStorageをクリア
      clearLocalStorage();
    },
  },
});
